<template>
    <div class="table-scroll" id="table-scroll-week">
        <table class="v-datatable v-table theme--light">
            <thead>
                <tr>
                    <th style="min-width: 200px; padding: 4px;" align="center">
                        <v-text-field
                            style="width: 200px;"
                            label="Location Name"
                            :value="localFilterLocationName"
                            @input="handleModifiedFilterLocationNameChanged"
                            prepend-inner-icon="search"
                            single-line
                            hide-details
                            dense
                            outline>
                        </v-text-field>
                    </th>
                    <th style="min-width: 150px;" class="pa-0">Variety</th>
                    <th></th>
                    <th v-for="(week, index) in detailedWeeks" 
                        :key="index" 
                        :class="`${week.isActiveWeek ? `info lighten-2 font-weight-bold` : ``} pa-0`"
                        style="min-width: 100px;"
                        @click="selectWeek(week.week)">
                        <div class="vertical-cell">
                            <v-badge v-model="week.publicHoliday" color="red">
                                <template v-slot:badge>
                                    <v-tooltip bottom>
                                        <template v-slot:activator="{ on }">
                                            <v-icon small light v-on="on">event</v-icon>
                                        </template>
                                        <span>Public Holiday/s</span>
                                        
                                         <ul>
                                            <li v-for="holidayName in week.days.map(d=> d.holidayName).filter(h=> h)"
                                                :key="holidayName">{{holidayName}}</li>
                                        </ul>
                                    </v-tooltip>
                                </template>
                                <v-layout align-center justify-center row fill-height>
                                    <div class="vertical-cell-left">
                                        <v-checkbox label="TY" class="pr-2" tabindex="-1" v-if="!week.locked && showYieldValues && week.yieldedCrops.length > 0" :value="forecastWeeksYieldApplied.filter(f=> f.week == week.week).length == week.yieldedCrops.length && week.yieldedCrops.length != 0" hide-details v-on:click.stop.prevent="applyWeekYield(week)"></v-checkbox>
                                        <v-checkbox label="P" class="pr-2" tabindex="-1" v-if="!week.locked && showProfileValues && week.profiledCrops.length > 0" :value="forecastWeeksProfileApplied.filter(f=> f.week == week.week).length == week.profiledCrops.length && week.profiledCrops.length != 0" hide-details v-on:click.stop.prevent="applyWeekProfile(week)"></v-checkbox>
                                    </div>                                    
                                    <span>WK{{ week.financialWeek }}</span>
                                </v-layout>
                            </v-badge>
                        </div>
                    </th>
                </tr>
            </thead>
            <tbody v-if="calendarSortedItems.length == 0">
                <tr>
                    <td :colspan="3 + detailedWeeks.length"></td>
                </tr>
            </tbody>
            <InfiniteLoadingTableBody v-else :data="calendarSortedItems" :page-size="12">
                <template v-slot:rows="{ shownData }">
                    <ForecastingOverviewCalendarWeekRow
                        v-for="(item, index) in shownData" 
                        :key="index"
                        :item="item"
                        :forecastWeeksYieldApplied="forecastWeeksYieldApplied"
                        :forecastWeeksProfileApplied="forecastWeeksProfileApplied"
                        @onYieldsApplied="$emit('onYieldsApplied', $event)"
                        @onProfilesApplied="$emit('onProfilesApplied', $event)"
                        @goToDetail="goToDetail(item)">
                    </ForecastingOverviewCalendarWeekRow>
                </template>
                <template v-slot:footerRows>
                    <tr class="fillerRow">
                        <td v-for="i in 3 + detailedWeeks.length" :key="i"></td>
                    </tr>
                </template>
            </InfiniteLoadingTableBody>
            <tfoot>
                <tr>
                    <td class="text-xs-center" colspan="2">
                        <v-layout row wrap>
                            <v-flex xs12>
                                <span>Total Active Locations in selection:</span>
                                <span class="ml-1 font-weight-bold">{{ calendarNumberOfLocationsFiltered }}</span>
                            </v-flex>
                            <v-flex xs12 v-if="showKG">
                                <span class="caption font-italic">Values shown in kilograms (kg)</span>
                            </v-flex>
                            <v-flex xs12 v-else>
                                <span class="caption font-italic">Values shown in trays</span>
                            </v-flex>
                            <v-flex xs12>
                                <span class="caption" ><span class="error--text" style="font-weight:bold;font-size:15;">!</span> indicates forecast volume against spray clash</span>
                            </v-flex>
                        </v-layout>
                    </td>
                    <td>
                        <v-layout column align-end>
                            <v-flex xs12>
                                <span>Total:</span>
                            </v-flex>
                            <v-flex xs12>
                                <span>ICA Total:</span>
                            </v-flex> 
                            <v-flex xs12>
                                <span>Actual Total:</span>
                            </v-flex>                           
                        </v-layout>
                    </td>
                    <td v-for="(week, index) in detailedWeeks" 
                        :key="index" 
                        :class="`${week.isActiveWeek ? `info lighten-2 font-weight-bold` : ``} pa-0`"
                        style="min-width: 100px;"
                        >
                        <div class="vertical-cell">
                            <v-layout column align-center>
                                <v-flex xs12>
                                    <span> 
                                        <v-icon v-if="!week.locked && editable" color="primary" @click="openBulkUpdateWeekDialog(week.week)">more_vert</v-icon>
                                        {{calculateWeekTotal(week.week)}}
                                        <v-icon v-if="week.locked">lock_outline</v-icon>
                                    </span>
                                </v-flex>
                                <v-flex xs12>
                                    <span>                                        
                                        {{calculateICAWeekTotal(week.week)}}                                        
                                    </span>
                                </v-flex>
                                <v-flex xs12>
                                    <span>                                        
                                        {{calculateActualWeekTotal(week.week)}}                                        
                                    </span>
                                </v-flex>
                            </v-layout>                            
                        </div> 
                    </td>
                </tr>
            </tfoot>
        </table>
        <ForecastingOverviewCalendarBulkUpdateDialog
            :dialog="dialog" 
            :selectedDate="selectedBulkUpdateDate"
            :rules="[validateForecastValue]"
            :isWeekMode="isWeekMode"
            :originalGrandTotal="selectedGrandTotal"
            :originalTotalSquareMeters="selectedTotalSquareMeters"
            @close="closeBulkUpdateWeekDialog">
        </ForecastingOverviewCalendarBulkUpdateDialog>
    </div>
 
</template>

<script>
import ForecastingOverviewCalendarWeekRow from '@/components/forecasting/ForecastingOverviewCalendarWeekRow.vue'
import ForecastingOverviewCalendarBulkUpdateDialog from '@/components/forecasting/ForecastingOverviewCalendarBulkUpdateDialog.vue'
import InfiniteLoadingTableBody from '@/components/generic/InfiniteLoadingTableBody.vue';
import * as Constants from '@/helpers/constants';

import { mapState, mapGetters, mapMutations, mapActions } from 'vuex';

export default {
    components: {
        ForecastingOverviewCalendarWeekRow,
        ForecastingOverviewCalendarBulkUpdateDialog,
        InfiniteLoadingTableBody
    },
    props: {
        forecastWeeksYieldApplied: {
            type: Array,
            required: true
        },
        forecastWeeksProfileApplied: {
            type: Array,
            required: true
        }
    },
    data(){
        return {
            dialog: false,
            selectedBulkUpdateDate: "",
            isWeekMode: false,
            selectedGrandTotal: 0,
            selectedTotalSquareMeters: 0,
            modifiedFilterLocationName: null,
            filterLocationNameTimeout: null,
        };
    },
    computed: {
        ...mapState("ForecastingStore", [
            "filterLocationName",
            "detailedWeeks",
            "calendarSortedItems",
            "calendarNumberOfLocationsFiltered",
            "calendarWeekTotals",
            "weekViewScrollPosition",
            "showYieldValues",
            "showProfileValues",
            "selectedUOM"
        ]),
        ...mapGetters("ForecastingStore", [
            "startOfSelectedWeek",
            "editable"
        ]),
        localFilterLocationName(){
            if(this.modifiedFilterLocationName != null){
                return this.modifiedFilterLocationName;
            }

            return this.filterLocationName;
        },
        showKG() {
            return this.selectedUOM === Constants.HARVESTPLAN_UOM_ENUM.KG.ID
        }
    },
    methods: {
        ...mapMutations("ForecastingStore", [
            "setFilterLocationName",
            "setWeekViewScrollPosition"
        ]),
        ...mapActions("ForecastingStore", [
            "updateData"
        ]),
        handleModifiedFilterLocationNameChanged(filterLocationName){
            this.modifiedFilterLocationName = filterLocationName;

            if(this.filterLocationNameTimeout != null){
                clearTimeout(this.filterLocationNameTimeout);
            }

            this.filterLocationNameTimeout = setTimeout(this.handleFilterLocationNameChanged, 200);
        },
        async handleFilterLocationNameChanged(){
            this.filterLocationNameTimeout = null;
            this.setFilterLocationName(this.modifiedFilterLocationName);
            await this.updateData();
            this.modifiedFilterLocationName = null;
        },
        async selectWeek(week){            
            this.$emit('onDateSelected', week);
        },
        goToDetail(item){
            this.$emit('goToDetail', {
                cropLocationId: item.cropLocationId,
                varietyId: item.varietyId
            });
        },
        calculateWeekTotal(week) {
           let weekValue = this.calendarWeekTotals.find(x => x.week == week);
            if(weekValue == null){
                return 0;
            }

            return weekValue.total;
        },
        calculateICAWeekTotal(week) {
            let weekValue = this.calendarWeekTotals.find(x => x.week == week);
            if(weekValue == null){
                return 0;
            }

            return weekValue.icaTotal;
        },
        calculateActualWeekTotal(week) {
            let weekValue = this.calendarWeekTotals.find(x => x.week == week);
            if(weekValue == null){
                return 0;
            }

            return weekValue.actualTotal;
        },
        calculateTotalSquareMeters(){
            return this.calendarSortedItems.reduce((a, v) => a + v.cropArea, 0);
        },
        openBulkUpdateWeekDialog(week) {
            this.selectedBulkUpdateDate = week;
            this.isWeekMode = true;
            this.selectedGrandTotal = this.calculateWeekTotal(week);
            this.selectedTotalSquareMeters = this.calculateTotalSquareMeters();

            this.dialog = true;
        },
        closeBulkUpdateWeekDialog() {
            this.dialog = false;
        },
        validateForecastValue(v){
            if(v == null || !v.match(/^[0-9]{1,5}$/)){
                return "Please enter a number in the form XXXXX";
            }

            return false;
        },
        applyWeekYield(week) {            
            this.$emit("applyWeekYield", week);
        },
        applyWeekProfile(week) {
            this.$emit("applyWeekProfile", week);
        }
    },
    created(){

    },
    mounted() {
        
    }
}
</script>

<style scoped>
.vertical-cell {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
}

.table-scroll {
    position: absolute;
    z-index: 1;
    overflow: auto;
    top: 0px;
    bottom: 0px;
    left: 0px;
    right: 0px;
    min-height: 100px;
}

.table-scroll table {
    width: 100%;
    height: 100%;
    margin: auto;
}

.table-scroll table tr td, .table-scroll table tr th {
    width: auto;
}

.table-scroll thead tr td, .table-scroll thead tr th {
    position: -webkit-sticky;
    position: sticky;
    background-color: white;
    z-index: 5;
}

.table-scroll thead tr:nth-child(1) td, .table-scroll thead tr:nth-child(1) th {
    top: 0px;
    height: 64px;
}

.table-scroll tfoot tr td, .table-scroll tfoot tr th {
    position: -webkit-sticky;
    position: sticky;
    background-color: white;
    z-index: 25;
}

.table-scroll tfoot tr:nth-child(1) td, .table-scroll tfoot tr:nth-child(1) th {
    bottom: 0px;
    height: 64px;
}

.table-scroll tfoot td:nth-child(1) {
    z-index: 25;
}

.table-scroll th:nth-child(1) {
    position: -webkit-sticky;
    position: sticky;
    left: 0;
    z-index: 25;
}

.table-scroll .fillerRow th, .fillerRow td {
    height: auto;
}

table thead th {
    font-size: 16px;;
}

</style>

